import { hasQueryStringParameter, getValueFromUrl, getCurrentUrl } from 'utils/window';
import { LOGIN_PATH } from 'constants/navigation';
import requester from 'core/http/requester';
import {
  USER_ALREADY_EXISTS,
  EMAIL_PASSWORD_COMBINATION_NOT_EXISTS,
  PASSWORD_RESET_SENDING_FAILED,
  UNKNOWN_ERROR,
  STATUS_OK
} from 'constants/progressStorage';

import { CONTENT_TYPES } from 'constants/common';

let constants = {
  tokenKey: 'token.auth.template',
  shortTermAccessKey: 'shortTermAccess.auth.template'
};

class ExternalStorageAuth {
  constructor(courseTitle, storageUrl) {
    this.courseTitle = courseTitle;
    this.storageUrl = storageUrl;
    this.courseLink = `${getCurrentUrl()}#${LOGIN_PATH}`;

    let isQueryStringAuth =
      hasQueryStringParameter('token') ||
      (hasQueryStringParameter('email') && hasQueryStringParameter('name'));

    if (!isQueryStringAuth) {
      return;
    }

    this.logout();
    this.token = getValueFromUrl('token');
  }

  set shortTermAccess(value) {
    if (value.toString().length) {
      localStorage.setItem(constants.shortTermAccessKey, value);
    } else {
      localStorage.removeItem(constants.shortTermAccessKey);
    }
  }

  get shortTermAccess() {
    return localStorage.getItem(constants.shortTermAccessKey) === 'true';
  }

  set token(value) {
    if (value) {
      localStorage.setItem(constants.tokenKey, value);
    } else {
      localStorage.removeItem(constants.tokenKey);
    }
  }

  get token() {
    return localStorage.getItem(constants.tokenKey);
  }

  getHeaders(contentType) {
    let headers = { 'x-access-token': this.token };
    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    return headers;
  }

  isAuthenticated() {
    return !!this.token;
  }

  async identify() {
    if (!this.token) {
      return;
    }

    let response = await requester.post(`${this.storageUrl}/user`, { headers: this.getHeaders() });

    if (response.status >= 400) {
      return this.logout();
    }

    return response.json();
  }

  logout() {
    this.token = '';
    this.shortTermAccess = '';
  }

  async exists(email) {
    let response = await requester.post(`${this.storageUrl}/user/exists`, {
      data: { email },
      headers: { 'Content-Type': CONTENT_TYPES.JSON },
      nocache: true
    });

    if (response.status === 409) {
      return USER_ALREADY_EXISTS;
    }

    return UNKNOWN_ERROR;
  }

  async authorize({ email, password, shortTermAccess = true }) {
    let response = await requester.post(`${this.storageUrl}/user/signin`, {
      data: { email, password, shortTermAccess },
      headers: { 'Content-Type': CONTENT_TYPES.JSON },
      nocache: true
    });

    return this._setAuthenticationData(response, shortTermAccess);
  }

  async register({ name, email, password, shortTermAccess = true }) {
    let response = await requester.post(`${this.storageUrl}/user/register`, {
      data: { email, password, name, shortTermAccess },
      headers: { 'Content-Type': CONTENT_TYPES.JSON },
      nocache: true
    });

    return this._setAuthenticationData(response, shortTermAccess);
  }

  async resetPassword(email) {
    let response = await requester.post(`${this.storageUrl}/user/forgotpassword`, {
      data: { email, returnUrl: this.courseLink },
      headers: { 'Content-Type': CONTENT_TYPES.JSON },
      nocache: true
    });

    if (response.status !== 200) {
      return PASSWORD_RESET_SENDING_FAILED;
    }

    return STATUS_OK;
  }

  async sendSecretLink({ email, password }) {
    let response = await requester.post(`${this.storageUrl}/link`, {
      data: {
        email,
        password: password || null,
        courseTitle: this.courseTitle,
        courseLink: this.courseLink,
        sendMail: true,
        returnLink: false
      },
      headers: { 'Content-Type': CONTENT_TYPES.JSON },
      nocache: true
    });

    if (response.status !== 200) {
      return UNKNOWN_ERROR;
    }

    return STATUS_OK;
  }

  async _setAuthenticationData(response, shortTermAccess) {
    if (response.status === 409) {
      return USER_ALREADY_EXISTS;
    }
    if (response.status === 403) {
      return EMAIL_PASSWORD_COMBINATION_NOT_EXISTS;
    }
    if (response.status >= 400) {
      return UNKNOWN_ERROR;
    }

    let data = await response.json();
    if (!data || (data && !data.token)) {
      return UNKNOWN_ERROR;
    }

    this.token = data.token;
    this.shortTermAccess = shortTermAccess;

    return STATUS_OK;
  }
}

export default ExternalStorageAuth;
